import React from "react";
import GuideLayout from "../../components/layout/GuideLayout";
import Section from "../../components/layout/Section";
import Subheader from "../../components/layout/Subheader";
import {
  Link,
  Typography,
} from "@mui/material";
import BlackFridayCountdown from "../../components/BlackFridayCountdown";

const sections = [
  { id: "intro", title: "intro" },
  { id: "what", title: "What's a BFGBC?" },
  { id: "special", title: "What makes BFGBCs special?" },
  { id: "valuation", title: "BFGBC valuations" },
  { id: "how", title: "How to Determine BFGBC" },
];

const BFGBC = () => {  
  return (
    <GuideLayout sections={sections}>
      <BlackFridayCountdown />
      <Section id="intro" title="All About BFGBCS">
        <Typography
          variant="caption"
          color="secondary"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          Last updated: September 2025
        </Typography>
        <Typography paragraph>
          Everything that you need to know about BFGBCs - what they are, why they're important, how to value - all the context you need!
        </Typography>
      </Section>

      <Section id="what" title="What is a BFGBC?">
        <Typography paragraph>
          BFGBC, which stands for Black Friday Gift Box Capsule, is a type of GBC (Gift Box Mystery Capsule) that is only available for purchase <b>one day a year</b> on Black Friday.
        </Typography>

        <Subheader title={"When is Black Friday?"} />
        <Typography paragraph>

          Specifically, it is the day after American Thanksgiving, which is always the 4th Thursday of November, meaning Black Friday will land on the 4th Friday of November. Black Friday traditionally marks the start of the Christmas shopping season and is often the busiest shopping day of the year in the United States. Many stores and companies offer highly discounted sales in order to encourage spending, and Neopets does this as well for the NC Mall.
        </Typography>
      </Section>

      <Section id="special" title="What makes BFGBCs special?">
        <Typography paragraph>
          Regular GBCs give out a minimum of 2 boxes, with a smaller chance of getting between 3-4 boxes. What makes BFGBCs so desirable to many traders is that they give a minimum of 4 boxes, with a small chance of getting between 5-6 boxes. BFGBCs are also cheaper than regular GBCs - they are sold for 100 NC compared to a regular GBC which is 200 NC. Many people will save their NC to specifically stock up on enough BFGBCs to last them throughout the year, especially heavy traders who go through boxes quickly.
        </Typography>

        <Typography paragraph>
          For more the full information on drop rates, please visit the <Link underline="none" href="https://classic.support.neopets.com/hc/en-us/articles/37898249845261-NC-Mall-Drop-Rates">official FAQ</Link> where TNT has finally published drop rates on NC items such as (BF)GBCS.
        </Typography>
      </Section>

      <Section id="valuation" title="What is the value of a BFGBC?">
        <Typography paragraph>
          In short, a BFGBC typically ranges 1-2 caps, however the value of a BFGBC can be a bit controversial in the NC Trading. Due to this, it is intentional that we do not give a straightfoward singular value other than the range. Instead, we will provide further context on how valuations may fluctuate so readers may make informed decisions before trading.
        </Typography>

        <Typography paragraph>
          First, it's important to note that the time of year does affect BFGBC valuations. Since BFGBCs can only be purchased in November, most people will value BFGBCs at 1 cap in the few months following between December - April, since there's usually plenty of supply. There is also a Spring Cleaning GBC sale towards the end of April where regular GBCs are available for purchase for 75 NC, which helps ease the "box drought" for those who maybe missed the Black Friday sale. Around June/July is when a lot of people may either be completely out of or running low on BFGBCs, so sometimes they offer higher value items "discounted" for BFGBCs. On the other end, those who have BFGBCs may be a lot pickier with what they trade them for. This often gives the impression that BFGBCs may be worth slightly more during the summer "box drought season".
        </Typography>
        <Typography paragraph>
          Valuation will also depend on who you ask. Some traders believe that since a BFGBC only cost 100 NC originally, it is unfair to value it higher than 1 cap since it was half the price of a regular GBC. There are also traders who feel that due to lack of supply as well as the amount of boxes they provide, it is more fair to value BFGBCs closer to 1.5 caps. There's also a few traders who value BFGBCs at 2 caps for similar reasons. There are also some traders who value it at 1 during the winter when BFGBCs are recently in the mall, but later bump that to 1.5 or 2 during the summer "box drought" season.
        </Typography>
        
        <Subheader
          title="Can BFGBCs be worth 0.5?"/>
        <Typography paragraph>
          This question often comes up with new traders because of the pricing logic. If 1 cap = 200NC and BFGBCs are 100NC, would they be worth 0.5? To be very honest, the realistic answer is no. Everyone is free to have their own personal values, so if you want to trade your own off at 0.5 each, you will find many takers, but you will have a <b>very</b> hard time finding someone to trade you their BFGBCs at a 0.5 value. Each item still requires a box to send and a BFGBC is still a GBC, which is why the NC community will still value it at a minimum of 1 cap.
        </Typography>

        <Typography paragraph>
          <b>Regardless of whichever value you may choose, it is a good rule of thumb to be consistent in your valuations!</b> This means to uphold NC Trading etiquette and trade fairly. For example, if you say you value your BFGBCs at 2, it is good etiquette to also trade for other people's BFGBCs at 2 during the <b>same timeframe</b>. It is generally frowned upon when people trade for BFGBCs stating a lower value, and then claim they value them higher when trading them off shortly after. Hence the memes of "my bfgbc is worth (big number) but ur bfgbc is worth 0.5" - it would be wise to not have that sort of reputation!
        </Typography>
        <Typography paragraph>
          <strong>As with all things NC, values are ultimately a personal decision, and you are under no obligation to trade with anyone whose values you disagree with.</strong> If you are unsure how a different party values their item, don't be afraid to ask!
        </Typography>
      </Section>

      <Section id="how" title="How do I know if it is a BFGBC?">
        <Typography paragraph>
          The BFGBC for any given year is always going to be the same design and have the same limited edition gift box as the regular GBC for the month of November.
        </Typography>

        <Typography paragraph>
          The most recent 2024 BFGBC, which was the 25th Anniversary Aisha Black Friday Gift Box Mystery Capsule, has "Black Friday" in the name, unlike the regular GBC version "25th Anniversary Aisha Gift Box Mystery Capsule". We hope TNT continues this naming pattern!
        </Typography>

        <Typography paragraph>
          Older BFGBCs were not so conveniently named and require additional steps to check. For example, the 2023 BFGBC was the Cutie Pie Weewoo Gift Box Mystery Capsule, which is the same name as the regular version. This BFGBC and the ones in years prior do not include any indication in the item name that it was different from the regular GBC version. The most common way to check which kind you have is to look at the description.
        </Typography>

        <Subheader title={'Item Descriptions'} />
        <Typography paragraph>
          The number of gift boxes in the description will let you know if you have the regular GBC version or the BFGBC version. You can view the description of items when you click on them in your inventory. Alternatively, if you are trading and do not have the item yet, you can hover over the item image when it is in your transfer log (desktop).
        </Typography>

        <Typography paragraph>
          In the regular GBC version of the Cutie Pie Weewoo Gift Box Mystery Capsule, the item description reads: <br /><i>This Weewoo baked this gift box to pie-fection! Open this capsule and receive one item worth 150 NC (or more) along with at least two Basic Gift Boxes. This capsule also has a chance of awarding a very special limited edition gift box bonus item!</i>
        </Typography>

        <Typography paragraph>
          The item description for the BFGBC version reads: <br /><i>This Weewoo baked this gift box to pie-fection! Open this capsule and receive one item worth 150 NC (or more) along with at least four Basic Gift Boxes. This capsule also has a chance of awarding a very special limited edition gift box bonus item!</i>
        </Typography>
      </Section>
    </GuideLayout>
  );
};

export default BFGBC;
